import { ToastProvider } from '@radix-ui/react-toast'
import { useApp } from '@tldraw/editor'
import classNames from 'classnames'
import React, { ReactNode } from 'react'
import { useValue } from 'signia-react'
import { DebugPanel, HelpMenu, NavigationZone, StylePanel } from '../components'
import { BackToContent } from '../components/BackToContent'
import { Dialogs } from '../components/Dialogs'
import { ExitPenMode } from '../components/ExitPenMode'
import { MenuZone } from '../components/MenuZone'
import { Button } from '../components/primitives'
import { Toasts, ToastViewport } from '../components/Toasts'
import { Toolbar } from '../components/Toolbar'
import {
	useActions,
	useAppEvents,
	useBreakpoint,
	useKeyboardShortcuts,
	useTranslation,
} from '../hooks'
import { useNativeClipboardEvents } from '../hooks/useClipboardEvents'
import { TldrawUiContextProvider, TldrawUiContextProviderProps } from './TldrawUiContextProvider'

/** @public */
export const TldrawUi = React.memo(function TldrawUi({
	shareZone,
	renderDebugMenuItems,
	children,
	...rest
}: {
	shareZone?: ReactNode
	renderDebugMenuItems?: () => React.ReactNode
	children?: any
} & TldrawUiContextProviderProps) {
	return (
		<TldrawUiContextProvider {...rest}>
			{children}
			<TldrawUiContent shareZone={shareZone} renderDebugMenuItems={renderDebugMenuItems} />
		</TldrawUiContextProvider>
	)
})

/** @public */
export const TldrawUiContent = React.memo(function TldrawUI({
	shareZone,
	renderDebugMenuItems,
}: {
	shareZone?: ReactNode
	renderDebugMenuItems?: () => React.ReactNode
}) {
	const app = useApp()
	const msg = useTranslation()
	const breakpoint = useBreakpoint()
	const isReadonlyMode = useValue('isReadOnlyMode', () => app.isReadOnly, [])
	const isFocusMode = useValue('isFocusMode', () => app.instanceState.isFocusMode, [])
	const isDebugMode = useValue('isDebugMode', () => app.instanceState.isDebugMode, [])

	useKeyboardShortcuts()
	useNativeClipboardEvents()
	useAppEvents()

	const { 'toggle-focus-mode': toggleFocus } = useActions()

	return (
		<ToastProvider>
			<main
				data-wd="desktop-ui"
				className={classNames('tlui-layout', { 'tlui-layout__mobile': breakpoint < 5 })}
			>
				{isFocusMode ? (
					<div className="tlui-layout__top">
						<Button
							className="tlui-focus-button"
							title={`${msg('focus-mode.toggle-focus-mode')}`}
							icon="dot"
							onClick={toggleFocus.onSelect}
						/>
					</div>
				) : (
					<>
						<div className="tlui-layout__top">
							<MenuZone />
							<div className="tlui-spacer" />
							<div className="tlui-share-zone" draggable={false}>
								{shareZone}
							</div>
						</div>
						<div
							className="tlui-layout__middle"
							data-wd="active-area"
							data-tldraw-area="active-drawing"
						>
							<div className="tlui-helper-buttons">
								<ExitPenMode />
								<BackToContent />
							</div>
							{breakpoint >= 5 && !isReadonlyMode && (
								<div className="tlui-style-panel__wrapper">
									<StylePanel />
								</div>
							)}
						</div>
						<div className="tlui-layout__bottom">
							<div className="tlui-layout__bottom__main">
								<NavigationZone />
								<Toolbar />
								{breakpoint >= 4 && <HelpMenu />}
							</div>
							{isDebugMode && <DebugPanel renderDebugMenuItems={renderDebugMenuItems ?? null} />}
						</div>
						<Toasts />
						<Dialogs />
						<ToastViewport />
					</>
				)}
			</main>
		</ToastProvider>
	)
})
